import { useState } from "react";
import { registerBlockType } from "@wordpress/blocks";
import {
    InspectorControls,
    useBlockProps,
    PanelColorSettings,
} from "@wordpress/block-editor";
import {
    PanelBody,
    __experimentalView as View,
    __experimentalText as Text,
    Button,
    CheckboxControl,
    SelectControl,
    TextControl,
    Popover,
    Modal,
    ToggleControl,
} from "@wordpress/components";
import UnitRange from "./components/UnitRange";
import proj4 from "proj4";

// Dodaj importy react-simple-maps
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
} from "react-simple-maps";

// Dodaj import pliku GeoJSON
import balticCountries from "../json/baltic-countries-topo.geojson";

// Importy MUI
import {
    Autocomplete,
    TextField,
    Box,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    Stack,
} from "@mui/material";

import { ColorPicker } from "@wordpress/components";

// Dodaj import geoMercator z d3-geo
import { geoMercator } from "d3-geo";

import ResponsiveRange from "./components/ResponsiveRange";

const LOCATION_TYPES = [
    { label: "Biuro", value: "office" },
    { label: "Fabryka", value: "factory" },
];

// Dodaj stałą z typami kursorów
const CURSOR_TYPES = [
    { label: "Domyślny", value: "default" },
    { label: "Wskaźnik", value: "pointer" },
];

// Funkcja do pobrania listy krajów z GeoJSON
const getCountriesFromGeoJSON = (geoJSON) => {
    return geoJSON.features.map((feature) => ({
        value: feature.properties.ISO3_CODE.toLowerCase(),
        label: feature.properties.NAME_ENGL,
        id: feature.properties.CNTR_ID,
    }));
};

// Pobierz listę krajów z pliku GeoJSON
const COUNTRIES = getCountriesFromGeoJSON(balticCountries);

// Definicja układów współrzędnych
proj4.defs(
    "EPSG:3035",
    "+proj=laea +lat_0=52 +lon_0=10 +x_0=4321000 +y_0=3210000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs"
);
proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs +type=crs");

// Funkcja do konwersji współrzędnych
function convertCoordinates(coordinates) {
    if (Array.isArray(coordinates[0])) {
        return coordinates.map((coord) => convertCoordinates(coord));
    }
    return proj4("EPSG:3035", "EPSG:4326", coordinates);
}

// Funkcja do konwersji całego GeoJSON
function convertGeoJSON(geoJSON) {
    const converted = JSON.parse(JSON.stringify(geoJSON));
    converted.features = geoJSON.features.map((feature) => {
        const newFeature = { ...feature };
        if (feature.geometry) {
            const coordinates = feature.geometry.coordinates;
            newFeature.geometry = {
                ...feature.geometry,
                coordinates: convertCoordinates(coordinates),
            };
        }
        return newFeature;
    });
    return converted;
}

// Konwersja danych GeoJSON
const convertedBalticCountries = convertGeoJSON(balticCountries);

// Dodaj współrzędne dla krajów
const COUNTRY_COORDINATES = {
    estonia: [58.5953, 25.0136],
    latvia: [56.8796, 24.6032],
    lithuania: [55.1694, 23.8813],
};

// Zaktualizuj konfigurację mapy
const MAP_CONFIG = {
    projection: "geoMercator",
    center: [24, 57], // Centrum mapy na kraje bałtyckie
    scale: 200,
    rotate: [0, 0, 0],
};

registerBlockType("danone/baltic-map", {
    apiVersion: 2,
    title: "Mapa Krajów Bałtyckich",
    icon: "location",
    category: "design",
    attributes: {
        locations: {
            type: "array",
            default: [],
        },
        selectedCountries: {
            type: "array",
            default: COUNTRIES.map((country) => country.value),
        },
        mapScale: {
            type: "number",
            default: 200,
        },
        mapCenter: {
            type: "array",
            default: [24, 57],
        },
        mapRotation: {
            type: "array",
            default: [0, 0, 0],
        },
        mapWidthDesktop: {
            type: "object",
            default: {
                value: 0,
                valueUnit: "px",
            },
        },
        mapWidthTablet: {
            type: "object",
            default: {
                value: 0,
                valueUnit: "px",
            },
        },
        mapWidthMobile: {
            type: "object",
            default: {
                value: 0,
                valueUnit: "px",
            },
        },
        mapHeightDesktop: {
            type: "object",
            default: {
                value: 0,
                valueUnit: "px",
            },
        },
        mapHeightTablet: {
            type: "object",
            default: {
                value: 0,
                valueUnit: "px",
            },
        },
        mapHeightMobile: {
            type: "object",
            default: {
                value: 0,
                valueUnit: "px",
            },
        },
        countryColors: {
            type: "object",
            default: {},
        },
        showCityLabels: {
            type: "boolean",
            default: true,
        },
        markerStyles: {
            type: "object",
            default: {
                office: {
                    color: "#007cba",
                    size: 4,
                    strokeWidth: 2,
                    strokeColor: "#fff",
                },
                factory: {
                    color: "#FF5733",
                    size: 4,
                    strokeWidth: 2,
                    strokeColor: "#fff",
                },
            },
        },
        labelStyles: {
            type: "object",
            default: {
                fontSize: 9,
                color: "#333",
                offset: 20,
            },
        },
    },

    edit: function BalticMapEdit({ attributes, setAttributes }) {
        const {
            locations,
            selectedCountries,
            mapScale,
            mapCenter,
            mapRotation,
            mapWidth,
            mapHeight,
            countryColors,
            showCityLabels,
            markerStyles,
            labelStyles,
        } = attributes;
        const blockProps = useBlockProps();

        // Stan do przechowywania wartości wyszukiwania
        const [searchTerm, setSearchTerm] = useState("");
        const [selectedCountryForColor, setSelectedCountryForColor] =
            useState(null);
        const [selectedCountryForModal, setSelectedCountryForModal] =
            useState(null);
        const [isAddingMarker, setIsAddingMarker] = useState(false);
        const [newLocation, setNewLocation] = useState({
            country: "",
            city: "",
            type: "office",
        });
        const [draggedMarker, setDraggedMarker] = useState(null);
        const [selectedMarker, setSelectedMarker] = useState(null);
        const [editingMarkerPosition, setEditingMarkerPosition] =
            useState(null);
        const [isAddingNewLocation, setIsAddingNewLocation] = useState(false);
        const [newMarkerCoordinates, setNewMarkerCoordinates] = useState(null);

        const handleCountryToggle = (countryValue) => {
            const currentIndex = selectedCountries.indexOf(countryValue);
            const newSelectedCountries = [...selectedCountries];

            if (currentIndex === -1) {
                newSelectedCountries.push(countryValue);
            } else {
                newSelectedCountries.splice(currentIndex, 1);
            }

            setAttributes({
                selectedCountries: newSelectedCountries,
            });
        };
        const updateValue = (
            name,
            device,
            direction,
            value,
            unit,
            isComplete
        ) => {
            const attributeName = `${name}${
                device.charAt(0).toUpperCase() + device.slice(1)
            }`;

                setAttributes({
                    [attributeName]: {
                        value: value,
                        valueUnit: unit,
                    },
                });
        };

        // Filtrowanie krajów po wyszukiwaniu
        const filteredCountries = COUNTRIES.filter((country) =>
            country.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const closeModal = () => {
            setSelectedCountryForModal(null);
        };

        const handleMapClick = (event) => {
            if (!isAddingMarker && !editingMarkerPosition) return;

            const svgElement = event.target.closest("svg");
            if (!svgElement) return;

            const bounds = svgElement.getBoundingClientRect();
            const x = event.clientX - bounds.left;
            const y = event.clientY - bounds.top;

            const projection = geoMercator()
                .center(mapCenter)
                .scale(mapScale)
                .rotate(mapRotation)
                .translate([bounds.width / 2, bounds.height / 2]);

            const coordinates = projection.invert([x, y]);

            if (editingMarkerPosition) {
                const newLocations = locations.map((loc) =>
                    loc.id === editingMarkerPosition
                        ? { ...loc, coordinates: coordinates }
                        : loc
                );
                setAttributes({ locations: newLocations });
                setEditingMarkerPosition(null);
            } else if (isAddingMarker) {
                setNewMarkerCoordinates(coordinates);
                setIsAddingNewLocation(true);
                setIsAddingMarker(false);
            }
        };

        const onMarkerDragStart = (event, markerId) => {
            event.stopPropagation(); // Prevent map click
            setDraggedMarker(markerId);
        };

        const onMarkerDragEnd = (event, markerId) => {
            if (!draggedMarker) return;

            const svgElement = event.target.closest("svg");
            if (!svgElement) return;

            const bounds = svgElement.getBoundingClientRect();
            const x = event.clientX - bounds.left;
            const y = event.clientY - bounds.top;

            const projection = geoMercator()
                .center(mapCenter)
                .scale(mapScale)
                .rotate(mapRotation)
                .translate([bounds.width / 2, bounds.height / 2]);

            const coordinates = projection.invert([x, y]);

            const newLocations = locations.map((location) => {
                if (location.id === markerId) {
                    return {
                        ...location,
                        coordinates: coordinates,
                    };
                }
                return location;
            });

            setAttributes({ locations: newLocations });
            setDraggedMarker(null);
        };

        const handleMarkerClick = (event, location) => {
            event.stopPropagation(); // Prevent map click
            setSelectedMarker(location);
        };

        return (
            <>
                <InspectorControls>
                    <PanelBody title="Ustawienia mapy" initialOpen={true}>
                        <View style={{ padding: "15px 0" }}>
                            <ResponsiveRange
                                type="single"
                                name="mapWidth"
                                label="Szerokość mapy"
                                attributes={{
                                    mapWidthDesktop: attributes.mapWidthDesktop,
                                    mapWidthTablet: attributes.mapWidthTablet,
                                    mapWidthMobile: attributes.mapWidthMobile,
                                }}
                                updateValue={updateValue}
                                onReset={(name, device) => {
                                    const newWidth = { ...attributes.mapWidth };
                                    delete newWidth[device];
                                    setAttributes({ mapWidth: newWidth });
                                }}
                                min={0}
                                max={1000}
                            />

                            <ResponsiveRange
                                type="single"
                                name="mapHeight"
                                label="Wysokość mapy"
                                attributes={{
                                    mapHeightDesktop: attributes.mapHeightDesktop,
                                    mapHeightTablet: attributes.mapHeightTablet,
                                    mapHeightMobile: attributes.mapHeightMobile,
                                }}
                                updateValue={updateValue}
                                onReset={(name, device) => {
                                    const newHeight = { ...attributes.mapHeight };
                                    delete newHeight[device];
                                    setAttributes({ mapHeight: newHeight });
                                }}
                                min={0}
                                max={1000}
                            />

                            <UnitRange
                                label="Skala mapy"
                                rangeValue={mapScale}
                                onRangeChange={(value) =>
                                    setAttributes({ mapScale: parseInt(value) })
                                }
                                unitValue={{ value: mapScale, unit: "" }}
                                onUnitChange={(value) =>
                                    setAttributes({
                                        mapScale: parseInt(value.value),
                                    })
                                }
                                min={100}
                                max={2000}
                                showUnits={false}
                                help="Wartość domyślna: 200"
                                style={{ marginBottom: "20px" }}
                            />

                            <UnitRange
                                label="Centrum mapy - Długość (X)"
                                rangeValue={mapCenter[0]}
                                onRangeChange={(value) =>
                                    setAttributes({
                                        mapCenter: [
                                            parseFloat(value),
                                            mapCenter[1],
                                        ],
                                    })
                                }
                                unitValue={{ value: mapCenter[0], unit: "" }}
                                onUnitChange={(value) =>
                                    setAttributes({
                                        mapCenter: [
                                            parseFloat(value.value),
                                            mapCenter[1],
                                        ],
                                    })
                                }
                                min={0}
                                max={50}
                                help="Wartość domyślna: 24"
                                style={{ marginBottom: "20px" }}
                            />

                            <UnitRange
                                label="Centrum mapy - Szerokość (Y)"
                                rangeValue={mapCenter[1]}
                                onRangeChange={(value) =>
                                    setAttributes({
                                        mapCenter: [
                                            mapCenter[0],
                                            parseFloat(value),
                                        ],
                                    })
                                }
                                unitValue={{ value: mapCenter[1], unit: "" }}
                                onUnitChange={(value) =>
                                    setAttributes({
                                        mapCenter: [
                                            mapCenter[0],
                                            parseFloat(value.value),
                                        ],
                                    })
                                }
                                min={50}
                                max={65}
                                help="Wartość domyślna: 57"
                                style={{ marginBottom: "20px" }}
                            />

                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setAttributes({
                                        mapScale: 200,
                                        mapCenter: [24, 57],
                                        mapRotation: [0, 0, 0],
                                    });
                                }}
                                style={{
                                    width: "100%",
                                    marginTop: "10px",
                                }}
                            >
                                Przywróć domyślne ustawienia mapy
                            </Button>
                        </View>

                        <hr
                            style={{
                                margin: "0 -16px 15px -16px",
                                border: "none",
                                borderTop: "1px solid #ddd",
                            }}
                        />

                        <View style={{ padding: "0 0 15px 0" }}>
                            <TextControl
                                label="Wyszukaj kraj"
                                value={searchTerm}
                                onChange={(value) => setSearchTerm(value)}
                                style={{
                                    marginBottom: "15px",
                                }}
                            />
                            <Box
                                sx={{
                                    width: "100%",
                                    maxHeight: "300px",
                                    overflow: "auto",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    padding: "10px",
                                    backgroundColor: "#fff",
                                }}
                            >
                                <List sx={{ padding: 0 }}>
                                    {filteredCountries.map((country) => {
                                        const isSelected =
                                            selectedCountries.includes(
                                                country.value
                                            );
                                        return (
                                            <ListItem
                                                key={country.value}
                                                onClick={() =>
                                                    handleCountryToggle(
                                                        country.value
                                                    )
                                                }
                                                sx={{
                                                    cursor: "pointer",
                                                    backgroundColor: isSelected
                                                        ? "rgba(0, 0, 0, 0.08)"
                                                        : "transparent",
                                                    padding: "4px 8px",
                                                    margin: "2px 0",
                                                    borderRadius: "4px",
                                                    "&:hover": {
                                                        backgroundColor:
                                                            "rgba(0, 0, 0, 0.04)",
                                                    },
                                                    height: "32px",
                                                }}
                                                dense
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    size="small"
                                                    sx={{
                                                        padding: "4px",
                                                        marginRight: "8px",
                                                        color: "#007cba",
                                                        "&.Mui-checked": {
                                                            color: "#007cba",
                                                        },
                                                    }}
                                                />
                                                <ListItemText
                                                    primary={country.label}
                                                    sx={{
                                                        margin: 0,
                                                        "& .MuiTypography-root":
                                                            {
                                                                fontSize:
                                                                    "13px",
                                                                lineHeight: "1",
                                                                whiteSpace:
                                                                    "nowrap",
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                            },
                                                    }}
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Box>
                            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setAttributes({
                                            selectedCountries: COUNTRIES.map(
                                                (country) => country.value
                                            ),
                                        });
                                    }}
                                    style={{
                                        width: "100%",
                                        justifyContent: "center",
                                        height: "32px",
                                        padding: "0 12px",
                                        fontSize: "11px",
                                    }}
                                >
                                    Zaznacz wszystkie
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setAttributes({
                                            selectedCountries: [],
                                        });
                                    }}
                                    style={{
                                        width: "100%",
                                        justifyContent: "center",
                                        height: "32px",
                                        padding: "0 12px",
                                        fontSize: "11px",
                                    }}
                                >
                                    Odznacz wszystkie
                                </Button>
                            </Stack>
                        </View>

                        <hr
                            style={{
                                margin: "0 -16px 15px -16px",
                                border: "none",
                                borderTop: "1px solid #ddd",
                            }}
                        />

                        <View style={{ padding: "15px 0 15px 0" }}>
                            <Button
                                isPrimary
                                onClick={() => setIsAddingMarker(true)}
                                style={{
                                    width: "100%",
                                    borderRadius: "0.5rem",
                                }}
                                disabled={isAddingMarker}
                            >
                                {isAddingMarker
                                    ? "Kliknij na mapie, aby dodać znacznik"
                                    : "Dodaj nową lokalizację"}
                            </Button>
                            {isAddingMarker && (
                                <Button
                                    isSecondary
                                    onClick={() => setIsAddingMarker(false)}
                                    style={{
                                        width: "100%",
                                        marginTop: "10px",
                                    }}
                                >
                                    Anuluj dodawanie znacznika
                                </Button>
                            )}
                        </View>

                        {/* Modal do wprowadzania danych nowej lokalizacji */}
                        {isAddingNewLocation && (
                            <Modal
                                title="Dodaj nową lokalizację"
                                onRequestClose={() => {
                                    setIsAddingNewLocation(false);
                                    setNewMarkerCoordinates(null);
                                    setNewLocation({
                                        country: "",
                                        city: "",
                                        type: "office",
                                    });
                                }}
                                className="wp-block-danone-baltic-map-modal"
                            >
                                <div style={{ padding: "20px" }}>
                                    <SelectControl
                                        label="Kraj"
                                        value={newLocation.country}
                                        options={COUNTRIES.filter((country) =>
                                            selectedCountries.includes(
                                                country.value
                                            )
                                        )}
                                        onChange={(value) =>
                                            setNewLocation({
                                                ...newLocation,
                                                country: value,
                                            })
                                        }
                                    />
                                    <TextControl
                                        label="Miasto"
                                        value={newLocation.city}
                                        onChange={(value) =>
                                            setNewLocation({
                                                ...newLocation,
                                                city: value,
                                            })
                                        }
                                    />
                                    <SelectControl
                                        label="Typ lokalizacji"
                                        value={newLocation.type}
                                        options={LOCATION_TYPES}
                                        onChange={(value) =>
                                            setNewLocation({
                                                ...newLocation,
                                                type: value,
                                            })
                                        }
                                    />
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            marginTop: "20px",
                                        }}
                                    >
                                        <Button
                                            isPrimary
                                            onClick={() => {
                                                if (
                                                    newLocation.country &&
                                                    newLocation.city
                                                ) {
                                                    setAttributes({
                                                        locations: [
                                                            ...locations,
                                                            {
                                                                id: Date.now(),
                                                                ...newLocation,
                                                                className: "",
                                                                cursor: "default",
                                                                coordinates:
                                                                    newMarkerCoordinates,
                                                            },
                                                        ],
                                                    });
                                                    setIsAddingNewLocation(
                                                        false
                                                    );
                                                    setNewMarkerCoordinates(
                                                        null
                                                    );
                                                    setNewLocation({
                                                        country: "",
                                                        city: "",
                                                        type: "office",
                                                    });
                                                }
                                            }}
                                            disabled={
                                                !newLocation.country ||
                                                !newLocation.city
                                            }
                                        >
                                            Zapisz
                                        </Button>
                                        <Button
                                            isSecondary
                                            onClick={() => {
                                                setIsAddingNewLocation(false);
                                                setNewMarkerCoordinates(null);
                                                setNewLocation({
                                                    country: "",
                                                    city: "",
                                                    type: "office",
                                                });
                                            }}
                                        >
                                            Anuluj
                                        </Button>
                                    </div>
                                </div>
                            </Modal>
                        )}
                    </PanelBody>

                    <PanelBody title="Znaczniki" initialOpen={false}>
                        <View
                            style={{
                                marginBottom: "20px",
                                padding: "25px 0 0 0",
                            }}
                        >
                            <ToggleControl
                                label="Pokaż nazwy miast"
                                checked={showCityLabels}
                                onChange={(value) =>
                                    setAttributes({ showCityLabels: value })
                                }
                            />
                        </View>

                        {LOCATION_TYPES.map((type) => (
                            <View
                                style={{
                                    marginTop: "30px",
                                    padding: "20px",
                                    backgroundColor: "#f8f8f8",
                                    borderRadius: "0.5rem",
                                }}
                            >
                                <Text
                                    as="h4"
                                    style={{
                                        margin: "0",
                                        padding: "0 0 10px 0",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                    }}
                                >
                                    {type.label}
                                </Text>

                                <View style={{ marginBottom: "25px" }}>
                                    <PanelColorSettings
                                        title="Kolory znacznika"
                                        colorSettings={[
                                            {
                                                value: markerStyles[type.value]
                                                    .color,
                                                onChange: (color) => {
                                                    setAttributes({
                                                        markerStyles: {
                                                            ...markerStyles,
                                                            [type.value]: {
                                                                ...markerStyles[
                                                                    type.value
                                                                ],
                                                                color,
                                                            },
                                                        },
                                                    });
                                                },
                                                label: "Kolor wypełnienia",
                                            },
                                            {
                                                value: markerStyles[type.value]
                                                    .strokeColor,
                                                onChange: (color) => {
                                                    setAttributes({
                                                        markerStyles: {
                                                            ...markerStyles,
                                                            [type.value]: {
                                                                ...markerStyles[
                                                                    type.value
                                                                ],
                                                                strokeColor:
                                                                    color,
                                                            },
                                                        },
                                                    });
                                                },
                                                label: "Kolor obramowania",
                                            },
                                        ]}
                                    />
                                </View>

                                <View style={{ marginBottom: "20px" }}>
                                    <UnitRange
                                        label="Rozmiar znacznika"
                                        rangeValue={
                                            markerStyles[type.value].size
                                        }
                                        onRangeChange={(value) => {
                                            setAttributes({
                                                markerStyles: {
                                                    ...markerStyles,
                                                    [type.value]: {
                                                        ...markerStyles[
                                                            type.value
                                                        ],
                                                        size: parseInt(value),
                                                    },
                                                },
                                            });
                                        }}
                                        min={2}
                                        max={10}
                                        help="Wartość domyślna: 4"
                                        showUnits={false}
                                    />
                                </View>

                                <View style={{ marginBottom: "10px" }}>
                                    <UnitRange
                                        label="Grubość obramowania"
                                        rangeValue={
                                            markerStyles[type.value].strokeWidth
                                        }
                                        onRangeChange={(value) => {
                                            setAttributes({
                                                markerStyles: {
                                                    ...markerStyles,
                                                    [type.value]: {
                                                        ...markerStyles[
                                                            type.value
                                                        ],
                                                        strokeWidth:
                                                            parseInt(value),
                                                    },
                                                },
                                            });
                                        }}
                                        min={0}
                                        max={5}
                                        help="Wartość domyślna: 2"
                                        showUnits={false}
                                    />
                                </View>
                            </View>
                        ))}

                        <View
                            style={{
                                marginTop: "30px",
                                padding: "20px",
                                backgroundColor: "#f8f8f8",
                                borderRadius: "4px",
                            }}
                        >
                            <Text
                                as="h4"
                                style={{
                                    margin: "0",
                                    padding: "0 0 10px 0",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                }}
                            >
                                Style etykiet
                            </Text>

                            <View style={{ marginBottom: "20px" }}>
                                <UnitRange
                                    label="Rozmiar czcionki"
                                    rangeValue={labelStyles.fontSize}
                                    onRangeChange={(value) => {
                                        setAttributes({
                                            labelStyles: {
                                                ...labelStyles,
                                                fontSize: parseInt(value),
                                            },
                                        });
                                    }}
                                    min={6}
                                    max={16}
                                    help="Wartość domyślna: 9"
                                    showUnits={false}
                                />
                            </View>

                            <View style={{ marginBottom: "20px" }}>
                                <UnitRange
                                    label="Odstęp od znacznika"
                                    rangeValue={labelStyles.offset}
                                    onRangeChange={(value) => {
                                        setAttributes({
                                            labelStyles: {
                                                ...labelStyles,
                                                offset: parseInt(value),
                                            },
                                        });
                                    }}
                                    min={10}
                                    max={40}
                                    help="Wartość domyślna: 20"
                                    showUnits={false}
                                />
                            </View>

                            <View style={{ marginBottom: "10px" }}>
                                <PanelColorSettings
                                    title="Kolor etykiety"
                                    colorSettings={[
                                        {
                                            value: labelStyles.color,
                                            onChange: (color) => {
                                                setAttributes({
                                                    labelStyles: {
                                                        ...labelStyles,
                                                        color,
                                                    },
                                                });
                                            },
                                            label: "Kolor tekstu",
                                        },
                                    ]}
                                />
                            </View>
                        </View>
                    </PanelBody>
                </InspectorControls>

                <div
                    {...blockProps}
                    style={{
                        width: `${attributes.mapWidthDesktop.value}${attributes.mapWidthDesktop.unit}`,
                        height: `${attributes.mapHeightDesktop.value}${attributes.mapHeightDesktop.unit}`,
                        cursor:
                            isAddingMarker || editingMarkerPosition
                                ? "crosshair"
                                : "default",
                        position: "relative",
                    }}
                >
                    <ComposableMap
                        projection={MAP_CONFIG.projection}
                        projectionConfig={{
                            center: mapCenter,
                            scale: mapScale,
                            rotate: mapRotation,
                        }}
                        style={{
                            width: `${attributes.mapWidthDesktop.value}${attributes.mapWidthDesktop.unit}`,
                            height: `${attributes.mapHeightDesktop.value}${attributes.mapHeightDesktop.unit}`,
                            outline: "none",
                            WebkitTapHighlightColor: "transparent",
                        }}
                        onClick={handleMapClick}
                    >
                        <Geographies geography={convertedBalticCountries}>
                            {({ geographies }) =>
                                geographies
                                    .filter((geo) =>
                                        selectedCountries.includes(
                                            geo.properties.ISO3_CODE.toLowerCase()
                                        )
                                    )
                                    .map((geo) => (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            onClick={() => {
                                                if (
                                                    !isAddingMarker &&
                                                    !editingMarkerPosition &&
                                                    selectedCountries.includes(
                                                        geo.properties.ISO3_CODE.toLowerCase()
                                                    )
                                                ) {
                                                    if (
                                                        selectedCountryForColor
                                                    ) {
                                                        setSelectedCountryForColor(
                                                            geo.properties
                                                                .ISO3_CODE
                                                        );
                                                    } else {
                                                        setSelectedCountryForModal(
                                                            geo.properties
                                                        );
                                                    }
                                                }
                                            }}
                                            style={{
                                                default: {
                                                    fill:
                                                        countryColors[
                                                            geo.properties
                                                                .ISO3_CODE
                                                        ] ||
                                                        (selectedCountries.includes(
                                                            geo.properties.ISO3_CODE.toLowerCase()
                                                        )
                                                            ? "#007cba"
                                                            : "#F5F4F6"),
                                                    outline: "none",
                                                    cursor:
                                                        isAddingMarker ||
                                                        editingMarkerPosition
                                                            ? "crosshair"
                                                            : "pointer",
                                                },
                                                hover: {
                                                    fill:
                                                        countryColors[
                                                            geo.properties
                                                                .ISO3_CODE
                                                        ] ||
                                                        (selectedCountries.includes(
                                                            geo.properties.ISO3_CODE.toLowerCase()
                                                        )
                                                            ? "#006ba1"
                                                            : "#F5F4F6"),
                                                    outline: "none",
                                                    cursor:
                                                        isAddingMarker ||
                                                        editingMarkerPosition
                                                            ? "crosshair"
                                                            : "pointer",
                                                },
                                                pressed: {
                                                    fill:
                                                        countryColors[
                                                            geo.properties
                                                                .ISO3_CODE
                                                        ] ||
                                                        (selectedCountries.includes(
                                                            geo.properties.ISO3_CODE.toLowerCase()
                                                        )
                                                            ? "#005a88"
                                                            : "#F5F4F6"),
                                                    outline: "none",
                                                    cursor:
                                                        isAddingMarker ||
                                                        editingMarkerPosition
                                                            ? "crosshair"
                                                            : "pointer",
                                                },
                                            }}
                                        />
                                    ))
                            }
                        </Geographies>

                        {locations.map((location) => (
                            <Marker
                                key={location.id}
                                coordinates={location.coordinates}
                                draggable={
                                    !isAddingMarker && !editingMarkerPosition
                                }
                                onDragStart={(e) =>
                                    onMarkerDragStart(e, location.id)
                                }
                                onDragEnd={(e) =>
                                    onMarkerDragEnd(e, location.id)
                                }
                                className={location.className}
                            >
                                <g
                                    onClick={(e) => {
                                        if (
                                            !isAddingMarker &&
                                            !editingMarkerPosition
                                        ) {
                                            handleMarkerClick(e, location);
                                        }
                                    }}
                                    style={{
                                        transform:
                                            draggedMarker === location.id
                                                ? "scale(1.1)"
                                                : "none",
                                        transition:
                                            "transform 0.2s ease-in-out",
                                        cursor: location.cursor || "default",
                                        pointerEvents:
                                            isAddingMarker ||
                                            editingMarkerPosition
                                                ? "none"
                                                : "all",
                                    }}
                                >
                                    <circle
                                        r={markerStyles[location.type].size}
                                        fill={markerStyles[location.type].color}
                                        stroke={
                                            markerStyles[location.type]
                                                .strokeColor
                                        }
                                        strokeWidth={
                                            markerStyles[location.type]
                                                .strokeWidth
                                        }
                                    />
                                    {showCityLabels && (
                                        <text
                                            textAnchor="middle"
                                            y={labelStyles.offset}
                                            style={{
                                                fontFamily: "Arial",
                                                fontSize: `${labelStyles.fontSize}px`,
                                                fill: labelStyles.color,
                                                fontWeight: "bold",
                                                textShadow:
                                                    "1px 1px 1px rgba(255,255,255,0.5)",
                                            }}
                                        >
                                            {location.city}
                                        </text>
                                    )}
                                </g>
                            </Marker>
                        ))}
                    </ComposableMap>

                    {selectedMarker && (
                        <Modal
                            title={`Edytuj znacznik: ${selectedMarker.city}`}
                            onRequestClose={() => setSelectedMarker(null)}
                            className="wp-block-danone-baltic-map-modal"
                        >
                            <div style={{ padding: "20px" }}>
                                <SelectControl
                                    label="Kraj"
                                    value={selectedMarker.country}
                                    options={COUNTRIES.filter((country) =>
                                        selectedCountries.includes(
                                            country.value
                                        )
                                    )}
                                    onChange={(value) => {
                                        const newLocations = locations.map(
                                            (loc) =>
                                                loc.id === selectedMarker.id
                                                    ? { ...loc, country: value }
                                                    : loc
                                        );
                                        setAttributes({
                                            locations: newLocations,
                                        });
                                        setSelectedMarker({
                                            ...selectedMarker,
                                            country: value,
                                        });
                                    }}
                                />
                                <TextControl
                                    label="Miasto"
                                    value={selectedMarker.city}
                                    onChange={(value) => {
                                        const newLocations = locations.map(
                                            (loc) =>
                                                loc.id === selectedMarker.id
                                                    ? { ...loc, city: value }
                                                    : loc
                                        );
                                        setAttributes({
                                            locations: newLocations,
                                        });
                                        setSelectedMarker({
                                            ...selectedMarker,
                                            city: value,
                                        });
                                    }}
                                />
                                <SelectControl
                                    label="Typ lokalizacji"
                                    value={selectedMarker.type}
                                    options={LOCATION_TYPES}
                                    onChange={(value) => {
                                        const newLocations = locations.map(
                                            (loc) =>
                                                loc.id === selectedMarker.id
                                                    ? { ...loc, type: value }
                                                    : loc
                                        );
                                        setAttributes({
                                            locations: newLocations,
                                        });
                                        setSelectedMarker({
                                            ...selectedMarker,
                                            type: value,
                                        });
                                    }}
                                />
                                <SelectControl
                                    label="Typ kursora"
                                    value={selectedMarker.cursor || "default"}
                                    options={CURSOR_TYPES}
                                    onChange={(value) => {
                                        const newLocations = locations.map(
                                            (loc) =>
                                                loc.id === selectedMarker.id
                                                    ? { ...loc, cursor: value }
                                                    : loc
                                        );
                                        setAttributes({
                                            locations: newLocations,
                                        });
                                        setSelectedMarker({
                                            ...selectedMarker,
                                            cursor: value,
                                        });
                                    }}
                                    help="Wybierz typ kursora dla tego znacznika"
                                />
                                <TextControl
                                    label="Klasa CSS"
                                    value={selectedMarker.className || ""}
                                    onChange={(value) => {
                                        const newLocations = locations.map(
                                            (loc) =>
                                                loc.id === selectedMarker.id
                                                    ? {
                                                          ...loc,
                                                          className: value,
                                                      }
                                                    : loc
                                        );
                                        setAttributes({
                                            locations: newLocations,
                                        });
                                        setSelectedMarker({
                                            ...selectedMarker,
                                            className: value,
                                        });
                                    }}
                                    help="Dodaj własną klasę CSS dla tego znacznika"
                                />
                                <div
                                    style={{
                                        marginTop: "20px",
                                        display: "flex",
                                        gap: "10px",
                                        flexDirection: "column",
                                    }}
                                >
                                    <p
                                        style={{
                                            margin: "0 0 10px 0",
                                            color: "#666",
                                        }}
                                    >
                                        Możesz przeciągnąć znacznik na mapie,
                                        aby zmienić jego położenie.
                                    </p>
                                    <div
                                        style={{ display: "flex", gap: "10px" }}
                                    >
                                        <Button
                                            isPrimary
                                            onClick={() =>
                                                setSelectedMarker(null)
                                            }
                                        >
                                            Zamknij
                                        </Button>
                                        <Button
                                            isSecondary
                                            onClick={() => {
                                                setEditingMarkerPosition(
                                                    selectedMarker.id
                                                );
                                                setSelectedMarker(null);
                                            }}
                                        >
                                            Zmień współrzędne
                                        </Button>
                                        <Button
                                            isDestructive
                                            onClick={() => {
                                                const newLocations =
                                                    locations.filter(
                                                        (loc) =>
                                                            loc.id !==
                                                            selectedMarker.id
                                                    );
                                                setAttributes({
                                                    locations: newLocations,
                                                });
                                                setSelectedMarker(null);
                                            }}
                                        >
                                            Usuń znacznik
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Modal>
                    )}
                </div>

                {selectedCountryForColor && (
                    <Popover
                        onClose={() => setSelectedCountryForColor(null)}
                        position="middle center"
                    >
                        <View style={{ padding: "16px" }}>
                            <ColorPicker
                                color={
                                    countryColors[selectedCountryForColor] ||
                                    "#007cba"
                                }
                                onChange={(color) => {
                                    setAttributes({
                                        countryColors: {
                                            ...countryColors,
                                            [selectedCountryForColor]: color,
                                        },
                                    });
                                }}
                            />
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    const newColors = { ...countryColors };
                                    delete newColors[selectedCountryForColor];
                                    setAttributes({ countryColors: newColors });
                                    setSelectedCountryForColor(null);
                                }}
                                style={{ marginTop: "10px", width: "100%" }}
                            >
                                Przywróć domyślny kolor
                            </Button>
                        </View>
                    </Popover>
                )}

                {selectedCountryForModal && (
                    <Modal
                        title={`Informacje o kraju: ${selectedCountryForModal.NAME_ENGL}`}
                        onRequestClose={closeModal}
                        className="wp-block-danone-baltic-map-modal"
                    >
                        <div style={{ padding: "20px 0" }}>
                            <p>
                                <strong>Kod kraju:</strong>{" "}
                                {selectedCountryForModal.ISO3_CODE}
                            </p>

                            <div
                                style={{
                                    marginTop: "15px",
                                    backgroundColor: "#f8f8f8",
                                    borderRadius: "4px",
                                }}
                            >
                                <PanelColorSettings
                                    title="Kolor kraju"
                                    colorSettings={[
                                        {
                                            value:
                                                countryColors[
                                                    selectedCountryForModal
                                                        .ISO3_CODE
                                                ] || "#007cba",
                                            onChange: (color) => {
                                                setAttributes({
                                                    countryColors: {
                                                        ...countryColors,
                                                        [selectedCountryForModal.ISO3_CODE]:
                                                            color,
                                                    },
                                                });
                                            },
                                            label: "Kolor wypełnienia",
                                            disableCustomColors: false,
                                        },
                                    ]}
                                >
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            const newColors = {
                                                ...countryColors,
                                            };
                                            delete newColors[
                                                selectedCountryForModal
                                                    .ISO3_CODE
                                            ];
                                            setAttributes({
                                                countryColors: newColors,
                                            });
                                        }}
                                        style={{
                                            marginTop: "10px",
                                            width: "100%",
                                        }}
                                    >
                                        Przywróć domyślny kolor
                                    </Button>
                                </PanelColorSettings>
                            </div>

                            <div style={{ marginTop: "20px" }}>
                                <p>
                                    <strong>Lokalizacje w kraju:</strong>
                                </p>
                                {locations
                                    .filter(
                                        (loc) =>
                                            loc.country ===
                                            selectedCountryForModal.ISO3_CODE.toLowerCase()
                                    )
                                    .map((location, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                marginTop: "10px",
                                                padding: "10px",
                                                backgroundColor: "#f0f0f0",
                                                borderRadius: "4px",
                                            }}
                                        >
                                            <p>
                                                <strong>Miasto:</strong>{" "}
                                                {location.city}
                                            </p>
                                            <p>
                                                <strong>Typ:</strong>{" "}
                                                {
                                                    LOCATION_TYPES.find(
                                                        (type) =>
                                                            type.value ===
                                                            location.type
                                                    )?.label
                                                }
                                            </p>
                                        </div>
                                    ))}
                            </div>

                            <Button
                                variant="secondary"
                                onClick={closeModal}
                                style={{ marginTop: "20px" }}
                            >
                                Zamknij
                            </Button>
                        </div>
                    </Modal>
                )}
            </>
        );
    },

    save: function BalticMapSave({ attributes }) {
        const {
            locations,
            selectedCountries,
            mapScale,
            mapCenter,
            mapRotation,
            mapWidthDesktop,
            mapHeightDesktop,
            mapWidthTablet,
            mapHeightTablet,
            mapWidthMobile,
            mapHeightMobile,
            countryColors,
            showCityLabels,
            markerStyles,
            labelStyles,
        } = attributes;
        const blockProps = useBlockProps.save();

        // Create CSS variables for responsive dimensions with fallbacks
        let responsiveStyles = {};
        
        mapWidthDesktop.value && mapWidthDesktop.valueUnit != 'px' && (responsiveStyles['--map-width-desktop'] = `${mapWidthDesktop.value}${mapWidthDesktop.valueUnit}`);
        mapWidthTablet.value && mapWidthTablet.valueUnit != 'px' && (responsiveStyles['--map-width-tablet'] = `${mapWidthTablet.value}${mapWidthTablet.valueUnit}`);
        mapWidthMobile.value && mapWidthMobile.valueUnit != 'px' && (responsiveStyles['--map-width-mobile'] = `${mapWidthMobile.value}${mapWidthMobile.valueUnit}`);
        mapHeightDesktop.value && mapHeightDesktop.valueUnit != 'px' && (responsiveStyles['--map-height-desktop'] = `${mapHeightDesktop.value}${mapHeightDesktop.valueUnit}`);
        mapHeightTablet.value && mapHeightTablet.valueUnit != 'px' && (responsiveStyles['--map-height-tablet'] = `${mapHeightTablet.value}${mapHeightTablet.valueUnit}`);
        mapHeightMobile.value && mapHeightMobile.valueUnit != 'px' && (responsiveStyles['--map-height-mobile'] = `${mapHeightMobile.value}${mapHeightMobile.valueUnit}`);

        return (
            <div {...blockProps}>
                <div
                    className="map-block"
                    data-locations={JSON.stringify(locations || [])}
                    data-selected-countries={JSON.stringify(selectedCountries || [])}
                    data-scale={mapScale || 200}
                    data-center-lat={mapCenter?.[1] || 57}
                    data-center-lng={mapCenter?.[0] || 24}
                    data-rotation={JSON.stringify(mapRotation || [0, 0, 0])}
                    data-country-colors={JSON.stringify(countryColors || {})}
                    data-show-city-labels={showCityLabels ?? true}
                    data-marker-styles={JSON.stringify(markerStyles || {})}
                    data-label-styles={JSON.stringify(labelStyles || {})}
                    style={{ ...responsiveStyles }}
                />
            </div>
        );
    },
});
